/*
 * Copyright 2025-2026, Airspan
 * All Rights Reserved.
 *
 */

import React, { useState, useRef, useEffect } from 'react';
import { useThemePreference } from '../../theme/hooks';
import { ThemePreference } from '../../theme/ThemeContext';
import { IconButton, Tooltip } from '@mui/material';

interface ThemeOption {
    value: ThemePreference;
    label: string;
    icon: string;           // Material Icons ligature
}

const THEME_OPTIONS: ThemeOption[] = [
    { value: 'light', label: 'Light', icon: 'light_mode' },
    { value: 'dark', label: 'Dark', icon: 'dark_mode' },
    { value: 'system', label: 'System', icon: 'contrast' },
];

/** Map preference → Material Icon ligature for the trigger button */
const PREF_ICON: Record<ThemePreference, string> = {
    light: 'light_mode',
    dark: 'dark_mode',
    system: 'contrast',
};

const ThemeToggle: React.FC = () => {
    const { preference, setPreference } = useThemePreference();
    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    return (
        <div ref={menuRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <Tooltip title="Theme">
                <IconButton
                    size="small"
                    aria-label="Change theme"
                    color="inherit"
                    onClick={() => setOpen((v) => !v)}
                >
                    <span className="material-icons" style={{ fontSize: 18 }}>
                        {PREF_ICON[preference]}
                    </span>
                </IconButton>
            </Tooltip>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 4,
                        minWidth: 160,
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'calc(var(--radius) - 2px)',
                        boxShadow: 'var(--shadow-card)',
                        zIndex: 1200,
                        overflow: 'hidden',
                        padding: 4,
                    }}
                >
                    <div style={{ padding: '6px 8px', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-foreground)' }}>
                        Theme
                    </div>
                    <div style={{ height: 1, margin: '4px -4px', backgroundColor: 'var(--color-muted)' }} />
                    {THEME_OPTIONS.map((opt) => {
                        const isActive = preference === opt.value;
                        return (
                            <div
                                key={opt.value}
                                role="menuitem"
                                tabIndex={0}
                                onClick={() => {
                                    setPreference(opt.value);
                                    setOpen(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setPreference(opt.value);
                                        setOpen(false);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 8px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    borderRadius: 'calc(var(--radius) - 4px)',
                                    color: isActive ? 'var(--color-accent-foreground)' : 'var(--color-foreground)',
                                    backgroundColor: isActive ? 'var(--color-accent)' : 'transparent',
                                    fontWeight: 400,
                                    transition: 'background-color 150ms ease',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-accent)';
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = isActive ? 'var(--color-accent)' : 'transparent';
                                }}
                            >
                                <span className="material-icons" style={{ fontSize: 16 }}>
                                    {opt.icon}
                                </span>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export { ThemeToggle };
export default ThemeToggle;
